/**
 * OntologyGraphViewer.tsx
 *
 * Ontology Integration v1.2 - Graph Viewer Component
 * Displays FAIR Rules as an interactive Cytoscape graph with compliance bars.
 *
 * Features:
 * - Cytoscape graph visualization (nodes/edges)
 * - Pillar compliance bars (F/A/I/R percentages)
 * - FAIR Rule cards with OWL/SHACL snippets
 * - Export buttons (TTL, JSON-LD, OWL, SHACL)
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CytoscapeGraph } from '../components/ontology-v1/CytoscapeGraph';
import { PillarComplianceBars } from '../components/ontology-v1/PillarComplianceBars';
import { FairRuleCards } from '../components/ontology-v1/FairRuleCards';
import { ClassHierarchyBrowser } from '../components/ontology-v1/ClassHierarchyBrowser';
import { EvidencePanel } from '../components/ontology-v1/EvidencePanel';
import { SparqlEditor } from '../components/ontology-v1/SparqlEditor';
import { W3CSHACLResultsPanel } from '../components/ontology-v1/W3CSHACLResultsPanel';
import { Button } from '../components/primitives/Button';

type OntologyNode = {
  id: string;
  label: string;
  type: 'Class' | 'Individual' | 'Property';
  parent?: string;
  pillar?: 'F' | 'A' | 'I' | 'R';
  meta?: Record<string, unknown>;
};

type OntologyEdge = {
  source: string;
  target: string;
  label: string;
  kind: string;
  pillar?: 'F' | 'A' | 'I' | 'R';
  status?: 'satisfied' | 'missing' | 'unknown' | 'not_applicable';
  satisfiedBy?: string[];
};

type FairRule = {
  pillar: 'F' | 'A' | 'I' | 'R';
  id: string;
  name: string;
  question: string;
  owl?: string;
  shacl?: string;
  swrl?: string;
};

type RuleResult = {
  ruleId: string;
  state: 'pass' | 'fail' | 'unknown' | 'not_applicable';
  evidenceIds?: string[];
  notApplicableReason?: string;
  evaluatedBy?: 'shacl' | 'supplementary' | 'none';
};

type PillarCompliance = {
  pillar: 'F' | 'A' | 'I' | 'R';
  percent: number;
  passed: number;
  total: number;
};

type GraphNode = {
  id: string;
  label: string;
  kind: 'Class' | 'Individual' | 'Property';
  pillar?: 'F' | 'A' | 'I' | 'R';
};

type GraphEdge = {
  id: string;
  source: string;
  target: string;
  label: string;
  kind?: 'abox' | 'tbox';
  pillar: 'F' | 'A' | 'I' | 'R';
  status: 'satisfied' | 'missing';
  satisfiedBy?: string[];
};

type GraphBundle = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  stats: { totalEdges: number; satisfied: number };
  buildTimestamp?: string;
};

type W3CViolation = {
  ruleId: string;
  severity: 'Violation' | 'Warning' | 'Info';
  focusNode: string;
  resultPath: string;
  message: string;
  value?: string;
};

type ValidationFinding = {
  ruleId: string;
  severity: 'Info' | 'Warning' | 'Violation';
  message: string;
  path?: string;
};

type W3CRawFinding = {
  shape: string;
  focus: string;
  message: string;
  severity: string;
};

type OntologyBundle = {
  version: 'v1';
  namespace: string;
  nodes: OntologyNode[];
  edges: OntologyEdge[];
  rules: FairRule[];
  ruleResults?: RuleResult[];
  validation: {
    findings: Array<{ ruleId: string; severity: string; message: string }>;
    summaryByPillar: PillarCompliance[];
    validationSkipped?: boolean;
    w3cFindings?: W3CRawFinding[];
    w3cConforms?: boolean;
  };
  exports?: {
    owl?: string;
    ttl?: string;
    jsonld?: string;
    shacl?: string;
  };
  graphBundle?: GraphBundle;
};

/**
 * Body of the FAIR ontology graph viewer. Extracted from the route-bound
 * OntologyGraphViewer so the FAIR tab in JourneyNavigator can mount it
 * inline. Takes jobId as a prop instead of pulling from useParams.
 */
export function OntologyGraphContent({ jobId }: { jobId: string | undefined }) {
  const [bundle, setBundle] = useState<OntologyBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRule, setSelectedRule] = useState<FairRule | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<OntologyEdge | null>(null);
  const [activeView, setActiveView] = useState<'graph' | 'rules' | 'hierarchy'>('graph');
  const [showSparqlEditor, setShowSparqlEditor] = useState(false);

  // Feature flags (read from environment or config)
  const enableSparql = typeof window !== 'undefined' &&
    (window as unknown as { ENABLE_SPARQL_QUERIES?: boolean }).ENABLE_SPARQL_QUERIES === true;

  useEffect(() => {
    if (!jobId) return;

    // Fetch OntologyBundle from v1.2 API
    fetch(`/analysis/${jobId}/ontology-data`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then((data: OntologyBundle) => {
        setBundle(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('[OntologyGraphViewer] Failed to load ontology:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [jobId]);

  if (loading) {
    return (
      <div className="ontology-graph-viewer loading">
        <div className="spinner">Loading FAIR ontology graph...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ontology-graph-viewer error">
        <h2>Error Loading Ontology</h2>
        <p>{error}</p>
        <p className="hint">
          Make sure <code>ENABLE_ONTOLOGY=true</code> is set in your environment.
        </p>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="ontology-graph-viewer empty">
        <p>No ontology data available for this job.</p>
      </div>
    );
  }

  // Use graphBundle for visualization (has kind field for abox/tbox filtering)
  const graphBundle = bundle.graphBundle || { nodes: [], edges: [], stats: { totalEdges: 0, satisfied: 0 } };

  // Transform w3cFindings to W3CViolation format expected by W3CSHACLResultsPanel
  function transformW3CFindings(raw: W3CRawFinding[] | undefined): W3CViolation[] {
    if (!raw) return [];
    return raw.map(f => ({
      ruleId: f.shape,
      severity: normalizeSeverity(f.severity),
      focusNode: f.focus,
      resultPath: f.shape,
      message: f.message
    }));
  }

  // Normalize severity string to expected union type
  function normalizeSeverity(severity: string): 'Violation' | 'Warning' | 'Info' {
    const normalized = severity.toLowerCase();
    if (normalized === 'violation' || normalized === 'error') return 'Violation';
    if (normalized === 'warning') return 'Warning';
    return 'Info';
  }

  // Transform validation findings to expected format
  function transformFindings(findings: Array<{ ruleId: string; severity: string; message: string }>): ValidationFinding[] {
    return findings.map(f => ({
      ruleId: f.ruleId,
      severity: normalizeSeverity(f.severity),
      message: f.message
    }));
  }

  const transformedW3CFindings = transformW3CFindings(bundle.validation.w3cFindings);
  const transformedValidation = {
    findings: transformFindings(bundle.validation.findings),
    summaryByPillar: bundle.validation.summaryByPillar
  };

  // Handle "View Evidence" clicks: find the matching edge and open the EvidencePanel
  const handleViewEvidence = (rule: FairRule) => {
    const edges = bundle.graphBundle?.edges || bundle.edges;
    const match = edges.find(
      (e: { satisfiedBy?: string[]; label?: string }) =>
        e.satisfiedBy?.includes(rule.id) ||
        (e as { label?: string }).label?.startsWith(rule.id + ':')
    );
    if (match) {
      const edgeAny = match as Record<string, unknown>;
      setSelectedEdge({
        source: String(edgeAny.source || 'dataset'),
        target: String(edgeAny.target || `pillar-${rule.pillar}`),
        label: String(edgeAny.label || `${rule.id}: ${rule.name}`),
        kind: String(edgeAny.kind || 'tbox'),
        pillar: rule.pillar,
        status: edgeAny.status as OntologyEdge['status'],
        satisfiedBy: edgeAny.satisfiedBy as string[] | undefined,
      });
    } else {
      // No matching edge found — create a synthetic one so the panel still opens
      setSelectedEdge({
        source: 'dataset',
        target: `pillar-${rule.pillar}`,
        label: `${rule.id}: ${rule.name}`,
        kind: 'tbox',
        pillar: rule.pillar,
        status: 'missing',
        satisfiedBy: [],
      });
    }
  };

  const handleExport = (format: 'ttl' | 'jsonld' | 'owl' | 'shacl') => {
    const url = `/analysis/${jobId}/ontology/export/${format}`;
    window.open(url, '_blank');
  };

  return (
    <div className="ontology-graph-viewer">
      <header className="ontology-header">
        <h1>FAIR Ontology Graph</h1>
        <p className="job-id">Job ID: {jobId}</p>
        <span className="version-badge">v{bundle.version}</span>

        <div className="header-actions">
          <Button
            helpId="ontology.graphView"
            className={`view-toggle ${activeView === 'graph' ? 'active' : ''}`}
            onClick={() => setActiveView('graph')}
          >
            Graph View
          </Button>
          <Button
            helpId="ontology.fairRules"
            className={`view-toggle ${activeView === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveView('rules')}
          >
            Rules View
          </Button>
          <Button
            helpId="ontology.classHierarchy"
            className={`view-toggle ${activeView === 'hierarchy' ? 'active' : ''}`}
            onClick={() => setActiveView('hierarchy')}
          >
            Hierarchy
          </Button>

          <div className="export-buttons">
            <Button
              helpId="btn.exportTTL"
              className="btn-export"
              onClick={() => handleExport('ttl')}
              title="Export as Turtle (TTL)"
            >
              TTL
            </Button>
            <Button
              helpId="btn.exportJSONLD"
              className="btn-export"
              onClick={() => handleExport('jsonld')}
              title="Export as JSON-LD"
            >
              JSON-LD
            </Button>
            <Button
              helpId="btn.exportOWL"
              className="btn-export"
              onClick={() => handleExport('owl')}
              title="Export as OWL/XML"
            >
              OWL
            </Button>
            <Button
              helpId="btn.exportSHACL"
              className="btn-export"
              onClick={() => handleExport('shacl')}
              title="Export SHACL shapes"
            >
              SHACL
            </Button>
          </div>

          {enableSparql && (
            <Button
              helpId="ontology.sparqlEditor"
              className="btn-sparql"
              onClick={() => setShowSparqlEditor(true)}
              title="Open SPARQL Editor"
              style={{
                marginLeft: '12px',
                padding: '6px 12px',
                backgroundColor: '#8b5cf6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              SPARQL
            </Button>
          )}
        </div>
      </header>

      {/* Pillar Compliance Bars */}
      <PillarComplianceBars
        compliance={bundle.validation.summaryByPillar}
      />

      {/* W3C SHACL Validation Results (Phase 2) */}
      {transformedW3CFindings.length > 0 && (
        <W3CSHACLResultsPanel
          findings={transformedW3CFindings}
          conforms={bundle.validation.w3cConforms}
        />
      )}

      {/* Main Content */}
      <div className="ontology-content">
        {activeView === 'graph' && (
          <CytoscapeGraph
            nodes={graphBundle.nodes}
            edges={graphBundle.edges}
            onNodeClick={(nodeId: string) => {
              setSelectedNode(nodeId);
              // Find rule for this node
              const rule = bundle.rules.find(r => r.id === nodeId);
              if (rule) {
                setSelectedRule(rule);
              }
            }}
            onEdgeClick={(edge) => {
              // Type assertion: CytoscapeGraph's GraphEdge is compatible with our local GraphEdge
              const typedEdge = edge as unknown as { source: string; target: string; label: string; kind?: string; pillar?: 'F' | 'A' | 'I' | 'R'; status?: 'satisfied' | 'missing' | 'unknown'; satisfiedBy?: string[] };
              // Convert to OntologyEdge for the EvidencePanel
              const ontologyEdge: OntologyEdge = {
                source: typedEdge.source,
                target: typedEdge.target,
                label: typedEdge.label,
                kind: typedEdge.kind || 'tbox',
                pillar: typedEdge.pillar,
                status: typedEdge.status,
                satisfiedBy: typedEdge.satisfiedBy
              };
              setSelectedEdge(ontologyEdge);
            }}
          />
        )}

        {activeView === 'rules' && (
          <FairRuleCards
            rules={bundle.rules}
            validation={transformedValidation}
            ruleResults={bundle.ruleResults || []}
            onRuleClick={handleViewEvidence}
          />
        )}

        {activeView === 'hierarchy' && (
          <ClassHierarchyBrowser
            nodes={bundle.nodes}
            onNodeSelect={(nodeId: string) => {
              setSelectedNode(nodeId);
              // Find rule for this node
              const rule = bundle.rules.find(r => r.id === nodeId);
              if (rule) {
                setSelectedRule(rule);
              }
            }}
            selectedNodeId={selectedNode || undefined}
          />
        )}
      </div>

      {/* Rule Detail Panel */}
      {selectedRule && (
        <div className="rule-detail-panel">
          <div className="panel-header">
            <h3>{selectedRule.id}: {selectedRule.name}</h3>
            <Button
              helpId="rule.details"
              className="btn-close"
              onClick={() => setSelectedRule(null)}
              aria-label="Close"
            >
              ×
            </Button>
          </div>

          <div className="panel-body">
            <p className="rule-question">{selectedRule.question}</p>

            {selectedRule.owl && (
              <div className="snippet-block">
                <h4>OWL</h4>
                <pre><code>{selectedRule.owl}</code></pre>
              </div>
            )}

            {selectedRule.shacl && (
              <div className="snippet-block">
                <h4>SHACL</h4>
                <pre><code>{selectedRule.shacl}</code></pre>
              </div>
            )}

            {selectedRule.swrl && (
              <div className="snippet-block">
                <h4>SWRL</h4>
                <pre><code>{selectedRule.swrl}</code></pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Evidence Panel (for edges) */}
      {selectedEdge && (
        <EvidencePanel
          edge={selectedEdge}
          onClose={() => setSelectedEdge(null)}
        />
      )}

      {/* SPARQL Editor (optional, feature-flagged) */}
      {enableSparql && showSparqlEditor && jobId && (
        <SparqlEditor
          jobId={jobId}
          onClose={() => setShowSparqlEditor(false)}
        />
      )}

      {/* Stats Footer */}
      <div className="ontology-stats">
        <div className="stat">
          <span className="label">Nodes:</span>
          <span className="value">{graphBundle.nodes.length}</span>
        </div>
        <div className="stat">
          <span className="label">Edges:</span>
          <span className="value">{graphBundle.edges.length}</span>
        </div>
        <div className="stat">
          <span className="label">Rules:</span>
          <span className="value">{bundle.rules.length}</span>
        </div>
        <div className="stat">
          <span className="label">Satisfied:</span>
          <span className="value">
            {graphBundle.stats.satisfied} / {graphBundle.stats.totalEdges}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Route-bound wrapper — pulls jobId from URL params, renders OntologyGraphContent.
 * Mounted at /analysis/:job_id/ontology-graph.
 */
export function OntologyGraphViewer() {
  const { job_id } = useParams<{ job_id: string }>();
  return <OntologyGraphContent jobId={job_id} />;
}
